import { z } from 'zod';
import { isoDate, uuid } from './common';

/**
 * Asking (PRD 30 §5): a question over what this person can open, answered
 * by a model that chooses how to look — list, search, read — and says, per
 * tool, whether what it found was complete or sampled and how many meetings
 * it could not open. "We never discussed this" and "I did not find it" are
 * different answers, and the reader must be able to tell.
 */
export const askScope = z.enum(['all', 'meeting', 'space', 'series', 'folder']);
export type AskScope = z.infer<typeof askScope>;

export const askQuestionBody = z.object({
  question: z.string().trim().min(3).max(600),
  scope: askScope.default('all'),
  /** A meeting id, a space id, or a series key — per `scope`. */
  scopeId: z.string().trim().min(1).max(200).optional(),
});
export type AskQuestionBody = z.infer<typeof askQuestionBody>;

export const strategyStep = z.object({
  tool: z.string(),
  summary: z.string(),
  complete: z.boolean(),
  count: z.number().int(),
  skipped: z.number().int(),
});
export type StrategyStep = z.infer<typeof strategyStep>;

export const citation = z.object({
  meetingId: uuid,
  title: z.string(),
  startedAt: isoDate,
  segmentId: uuid.nullable(),
  text: z.string().nullable(),
  speakerName: z.string().nullable(),
  spokeAt: isoDate.nullable(),
});
export type Citation = z.infer<typeof citation>;

export const answer = z.object({
  id: uuid,
  question: z.string(),
  body: z.string(),
  /** False when the honest answer is "nothing" — never the nearest miss. */
  found: z.boolean(),
  scope: askScope,
  scopeId: z.string().nullable(),
  strategy: z.array(strategyStep),
  /** Meetings in the workspace that matched but this person cannot open. */
  skippedMeetings: z.number().int(),
  citations: z.array(citation),
  model: z.string().nullable(),
  keptAt: isoDate.nullable(),
  createdAt: isoDate,
});
export type Answer = z.infer<typeof answer>;

export const answersQuery = z.object({
  scope: askScope.optional(),
  scopeId: z.string().optional(),
  kept: z.enum(['1']).optional(),
});
export type AnswersQuery = z.infer<typeof answersQuery>;
